<script setup>
// Form de grupo de permissão - nome, descrição e catálogo de permissões
// renderizado por categoria. Grupos do sistema (system_default) ficam em
// modo somente-leitura com banner explicativo.
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { usePermissions } from 'dashboard/composables/usePermissions';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: v => ['CREATE', 'EDIT'].includes(v),
  },
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      permissions: [],
      systemDefault: false,
    }),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();
const { hasPermission } = usePermissions();

const availablePermissions = useMapGetter(
  'permissionGroups/getAvailablePermissions'
);
const flags = useMapGetter('permissionGroups/getUIFlags');

const state = reactive({
  name: '',
  description: '',
  permissions: [],
  systemDefault: false,
});

// Categorias colapsáveis - abertas por padrão na criação.
const expandedCategories = ref({});

const catalog = computed(() => availablePermissions.value?.catalog || {});

const categoryKeys = computed(() => Object.keys(catalog.value));

const canManage = computed(() => hasPermission('settings.permission_groups'));

const isLocked = computed(() => state.systemDefault || !canManage.value);

const isValid = computed(() => state.name.trim().length > 0 && !isLocked.value);

const submitLabel = computed(() =>
  props.mode === 'CREATE'
    ? t('PERMISSION_GROUPS.FORM.CREATE_BUTTON')
    : t('PERMISSION_GROUPS.FORM.SAVE_BUTTON')
);

const categoryLabel = key =>
  t(`PERMISSION_GROUPS.CATEGORIES.${key.toUpperCase()}`);

const permissionLabel = (category, action) => {
  const key = `PERMISSION_GROUPS.PERMISSIONS.${category.toUpperCase()}.${action.toUpperCase()}`;
  const translated = t(key);
  if (translated === key) {
    return action.replace(/_/g, ' ');
  }
  return translated;
};

// Descrição curta do que a permissão habilita (mostrada abaixo do rótulo).
const permissionDescription = (category, action) => {
  const key = `PERMISSION_GROUPS.DESCRIPTIONS.${category.toUpperCase()}.${action.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? '' : translated;
};

const permissionKey = (category, action) => `${category}.${action}`;

const isChecked = (category, action) =>
  state.permissions.includes(permissionKey(category, action));

const togglePermission = (category, action) => {
  if (isLocked.value) return;
  const key = permissionKey(category, action);
  const idx = state.permissions.indexOf(key);
  if (idx >= 0) {
    state.permissions.splice(idx, 1);
  } else {
    state.permissions.push(key);
  }
};

const toggleCategory = category => {
  expandedCategories.value[category] = !expandedCategories.value[category];
};

const selectAllInCategory = category => {
  if (isLocked.value) return;
  const actions = catalog.value[category] || [];
  const keys = actions.map(action => permissionKey(category, action));
  const merged = new Set([...state.permissions, ...keys]);
  state.permissions = Array.from(merged);
};

const clearAllInCategory = category => {
  if (isLocked.value) return;
  const actions = catalog.value[category] || [];
  const keys = new Set(actions.map(action => permissionKey(category, action)));
  state.permissions = state.permissions.filter(p => !keys.has(p));
};

const selectedInCategory = category => {
  const actions = catalog.value[category] || [];
  return actions.filter(action => isChecked(category, action)).length;
};

const resetForm = () => {
  Object.assign(state, {
    name: '',
    description: '',
    permissions: [],
    systemDefault: false,
  });
};

const handleSubmit = () => {
  if (!isValid.value || props.isLoading) return;
  emit('submit', {
    name: state.name.trim(),
    description: state.description.trim(),
    permissions: [...state.permissions],
  });
};

watch(
  () => props.initialData,
  newData => {
    if (!newData) return;
    Object.assign(state, {
      name: newData.name || '',
      description: newData.description || '',
      permissions: [...(newData.permissions || [])],
      systemDefault: !!newData.systemDefault,
    });
  },
  { immediate: true, deep: true }
);

// Expande todas as categorias por padrão quando catalog chega.
watch(
  categoryKeys,
  keys => {
    keys.forEach(k => {
      if (expandedCategories.value[k] === undefined) {
        expandedCategories.value[k] = true;
      }
    });
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('permissionGroups/fetchAvailablePermissions');
});

defineExpose({ resetForm });
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <!-- Banner pra grupo do sistema -->
    <div
      v-if="state.systemDefault"
      class="flex items-start gap-3 p-4 rounded-xl bg-n-amber-3 border border-n-amber-7 mb-4"
    >
      <i class="i-lucide-lock size-5 text-n-amber-11 flex-shrink-0 mt-0.5" />
      <div class="flex-1">
        <p class="text-sm font-medium text-n-amber-12 mb-0.5">
          {{ $t('PERMISSION_GROUPS.SYSTEM_BANNER.TITLE') }}
        </p>
        <p class="text-xs text-n-amber-11 mb-0">
          {{ $t('PERMISSION_GROUPS.SYSTEM_BANNER.MESSAGE') }}
        </p>
      </div>
    </div>

    <!-- Identificação -->
    <div class="flex flex-col gap-4 pb-4">
      <div class="flex items-center gap-6">
        <WithLabel
          :label="$t('PERMISSION_GROUPS.FORM.NAME.LABEL')"
          name="name"
          class="w-full"
        >
          <div class="flex-1">
            <Input
              v-model="state.name"
              type="text"
              :placeholder="$t('PERMISSION_GROUPS.FORM.NAME.PLACEHOLDER')"
              :disabled="isLocked"
            />
          </div>
        </WithLabel>
      </div>

      <div class="flex items-center gap-6">
        <WithLabel
          :label="$t('PERMISSION_GROUPS.FORM.DESCRIPTION.LABEL')"
          name="description"
          class="w-full"
        >
          <div class="flex-1">
            <Input
              v-model="state.description"
              type="text"
              :placeholder="
                $t('PERMISSION_GROUPS.FORM.DESCRIPTION.PLACEHOLDER')
              "
              :disabled="isLocked"
            />
          </div>
        </WithLabel>
      </div>
    </div>

    <hr class="border-n-weak my-6" />

    <!-- Catálogo de permissões -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between mb-1">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
            {{ $t('PERMISSION_GROUPS.FORM.PERMISSIONS.LABEL') }}
          </h3>
          <p class="text-xs text-n-slate-11 mb-0">
            {{ $t('PERMISSION_GROUPS.FORM.PERMISSIONS.HELP') }}
          </p>
        </div>
      </div>

      <div
        v-if="flags.isFetchingPermissions && categoryKeys.length === 0"
        class="text-sm text-n-slate-11 py-4"
      >
        {{ $t('PERMISSION_GROUPS.FORM.PERMISSIONS.LOADING') }}
      </div>

      <div
        v-for="category in categoryKeys"
        :key="category"
        class="rounded-xl border border-n-weak overflow-hidden"
      >
        <button
          type="button"
          class="w-full px-4 py-3 flex items-center gap-3 text-left bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
          @click="toggleCategory(category)"
        >
          <i
            class="size-4 text-n-slate-11 flex-shrink-0"
            :class="
              expandedCategories[category]
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
          <span class="text-sm font-semibold text-n-slate-12 flex-1">
            {{ categoryLabel(category) }}
          </span>
          <span class="text-xs text-n-slate-11 font-mono">
            {{ selectedInCategory(category) }} /
            {{ (catalog[category] || []).length }}
          </span>
        </button>

        <div
          v-if="expandedCategories[category]"
          class="px-4 py-3 border-t border-n-weak flex flex-col gap-3"
        >
          <div class="flex items-center gap-2">
            <NextButton
              type="button"
              faded
              slate
              size="xs"
              :label="$t('PERMISSION_GROUPS.FORM.PERMISSIONS.SELECT_ALL')"
              :disabled="isLocked"
              @click="selectAllInCategory(category)"
            />
            <NextButton
              type="button"
              faded
              slate
              size="xs"
              :label="$t('PERMISSION_GROUPS.FORM.PERMISSIONS.CLEAR_ALL')"
              :disabled="isLocked"
              @click="clearAllInCategory(category)"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label
              v-for="action in catalog[category]"
              :key="`${category}.${action}`"
              class="flex items-start gap-2.5 px-3 py-2 rounded-lg border border-n-weak hover:border-n-slate-7 transition-colors cursor-pointer"
              :class="{
                'opacity-60 cursor-not-allowed': isLocked,
                'border-n-brand/40 bg-n-brand/5': isChecked(category, action),
              }"
            >
              <Checkbox
                :model-value="isChecked(category, action)"
                :disabled="isLocked"
                class="mt-0.5 shrink-0"
                @change="togglePermission(category, action)"
              />
              <span class="flex-1 min-w-0">
                <span class="block text-sm font-medium text-n-slate-12 leading-tight">{{
                  permissionLabel(category, action)
                }}</span>
                <span
                  v-if="permissionDescription(category, action)"
                  class="block text-label-small text-n-slate-11 mt-0.5 leading-snug"
                  >{{ permissionDescription(category, action) }}</span
                >
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer actions -->
    <div
      class="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-n-weak"
    >
      <NextButton
        type="button"
        faded
        slate
        :label="$t('PERMISSION_GROUPS.FORM.CANCEL')"
        @click="$emit('cancel')"
      />
      <NextButton
        type="submit"
        :label="submitLabel"
        :disabled="!isValid || isLoading"
        :is-loading="isLoading"
      />
    </div>
  </form>
</template>
